import type { VariableType } from '@idc-forms/formula/types'

/**
 * LA FORMA CANÓNICA DE UNA VARIABLE — el metadato, y sólo el metadato (ADR 0036 §1).
 *
 * Una «variable» es un dato de la plataforma que alguien puede insertar en un texto o comparar en
 * una condición. Hasta el ADR 0036 había DOS catálogos disjuntos que ningún archivo relacionaba —
 * el del motor de documentos en el bundle de la web y el del binding de Créditos en la API—, con
 * dos formas del metadato, cuatro declaraciones del vocabulario de tipos y seis tablas de
 * traducción entre ellas. Esto es la forma única.
 *
 * ── EL CORTE ES METADATO ≠ RESOLUCIÓN ────────────────────────────────────────
 *
 * ⛔ Acá NO vive ningún `resolve`. El front sigue resolviendo documentos contra
 * `NegotiationVariableContext` y el kernel sigue resolviendo créditos contra los hechos del
 * expediente: subir cualquiera de los dos arrastraría su mundo (tipos de la web, la instancia de
 * Mongo) a un paquete que las dos apps compilan. Cada `defineVariable` del host REFERENCIA el
 * metadato de acá y se queda con su resolución al lado.
 *
 * ── SIN BARRELS ──────────────────────────────────────────────────────────────
 *
 * ⚠️ Este módulo no puede convertirse en un barril (regla 5 del monorepo, y el ADR la repite para
 * él): se importa del archivo concreto, como ya se hace con `api-v2/*` y `primitives/*`.
 */

/**
 * El vocabulario canónico SIN `enum`.
 *
 * `enum` está en desuso (ADR 0036 §3): el dominio cerrado se declara con `options` y `enum` ya es
 * la DERIVACIÓN de que las haya. Los catálogos que nacieron después del corte —el binding de
 * Créditos y los tokens de etapa— nunca lo escriben, y el tipo lo dice. El de documentos angosta
 * distinto, porque lo escribe en 14 lugares hasta que la ola 3 lo jubile.
 */
export type NonEnumVariableType = Exclude<VariableType, 'enum'>

/** Una opción de un dominio cerrado. `value` es la JOIN KEY y es estable; `label` es traducción. */
export type VariableOption = {
  value: string
  label: string
}

/**
 * DÓNDE APLICA una variable, y el criterio es el CONTEXTO DE RESOLUCIÓN — qué datos hay a mano
 * cuando alguien pregunta por el valor—, nunca el widget que la muestra (ADR 0036 §2).
 *
 * | scope | quién resuelve | qué tiene a mano |
 * |---|---|---|
 * | `document` | el `resolve(ctx)` del front, en impresión y preview | `NegotiationVariableContext` |
 * | `credit_notice` | `noticeVariableValues` / `creditNoticeFacts`, cuando dispara un efecto | expediente + negociación + la colección de persona |
 * | `credit_instance` | `stageVisibilityFacts`, en cada lectura y cada avance | el documento del expediente, y nada más |
 *
 * Son TRES y no cuatro: el chip que imprime y el bloque condicional del mismo correo corren en el
 * mismo contexto y se distinguen por `insertable`, que es un eje aparte. Un scope por panel
 * crecería con cada pantalla nueva.
 *
 * **Declarativo, no derivado de capacidades.** El caso «aplica acá pero todavía no se resuelve
 * acá» existe y está documentado —el binding en el `showWhen`—, y un scope derivado de «¿hay
 * resolver?» cambiaría solo el día que alguien agregue el resolver. Además el declarativo se
 * audita con un grep.
 */
export const VARIABLE_SCOPE = {
  DOCUMENT: 'document',
  CREDIT_NOTICE: 'credit_notice',
  CREDIT_INSTANCE: 'credit_instance'
} as const
export type VariableScope = (typeof VARIABLE_SCOPE)[keyof typeof VARIABLE_SCOPE]

/**
 * Máscaras de PRESENTACIÓN aplicables a un valor ya resuelto (número → letras, monto → moneda).
 *
 * Sólo la clave vive acá: la tabla `MaskDef` con su `apply` se queda en el host, porque transformar
 * es resolución y el corte de este paquete es el metadato. Una variable declara `masks` para
 * ACOTAR o FORZAR lo que se le ofrece; sin ellas se derivan del `type`.
 */
export type VariableMaskKey =
  | 'none'
  | 'letters'
  | 'lettersDecimal'
  | 'uppercase'
  | 'lowercase'
  | 'currency'
  | 'currencyLetters'
  | 'dateLetters'
  | 'dateNumbers'

/**
 * El metadato canónico. Lo que se declara UNA vez y las dos apps leen.
 *
 * `category` es `string` a propósito: documentos la tiene cerrada en nueve valores y Créditos la
 * arma con el nombre de la etapa. Cada catálogo la angosta en SU alias (ver
 * `DocumentVariableMetadata`), que es donde la restricción es cierta.
 */
export type VariableMetadata = {
  /** El token que se guarda, sin llaves: `valor_total`, `cliente.nombre`, `etapa2.bank`. */
  key: string
  /** Lo que ve una persona. Jamás la `key`. */
  label: string
  type: VariableType
  /** Dónde aplica. Nunca vacío: una variable que no aplica en ningún lado no existe. */
  scopes: readonly VariableScope[]
  /** Agrupador del selector: `negotiation`, «Etapa 2 · Integración de expediente». */
  category: string
  /** Subgrupo dentro de la categoría — sólo presentación: Categoría › Grupo › campo. */
  group?: string
  description?: string
  /** Dominio cerrado, cuando el paquete lo puede declarar. Ver `optionsFromHost`. */
  options?: readonly VariableOption[]
  /**
   * ☠️ El dominio cerrado EXISTE pero lo aporta el host, y el adaptador DEBE inyectarlo.
   *
   * No es una excepción de conveniencia: las listas de país / departamento / municipio salen de
   * 170 KB de JSON que viven en el bundle de la web, y las de tipo de cliente / género / plazo
   * salen de los catálogos que ya usan los formularios. Copiar cualquiera de las dos acá sería
   * fabricar la SEGUNDA fuente de verdad que este paquete existe para matar, y arrastrar los JSON
   * metería 170 KB de geografía en el bundle de la API a cambio de nada.
   *
   * La marca es la costura, y tiene sus dos mitades probadas: no puede convivir con `options`, y
   * el adaptador que no la respete deja el catálogo inválido (ver `catalog-integrity.ts`).
   */
  optionsFromHost?: true
  /** Valor de muestra para el panel de detalle y la vista previa. */
  example?: string | number | boolean
  /**
   * `false` = sirve para comparar pero no para imprimir. Un id impreso en un correo es un
   * tecnicismo frente al usuario. Ausente = insertable.
   */
  insertable?: boolean
  masks?: readonly VariableMaskKey[]
}

/**
 * Un catálogo DECLARADO, con nombre.
 *
 * El nombre no es decoración: es lo que hace que el barrido de seguridad pueda decir CUÁL catálogo
 * violó la frontera, y es la clave con la que el test del paquete verifica que no quedó ninguno
 * sin barrer.
 */
export type DeclaredCatalog = {
  name: string
  variables: readonly VariableMetadata[]
}
